<script setup lang="ts">
import type { HtmlParserPlugins } from '~/plugins/dom-parser'
import type { BookAndArticle, Page } from '~/utils/solr/literature'
import { getBodyPageNumbers, splitVirtualPageSegments } from '~/utils/literature/virtual-page-segments'
import { handleHighlights } from '~/utils/solr/highlighting-utils'
import { hidePageNumberOnSecondLevelPageNodeType } from '~/utils/solr/literature'

const props = defineProps<{
  page: Page
  book: BookAndArticle
  searchResult: { id: string, highlights: string[] } | null
  footnotes: FootnotesState
}>()

const emit = defineEmits<{
  mounted: [element: HTMLElement, pageNumber: string]
  beforeUnmount: [element: HTMLElement | null, pageNumber: string]
  openFootnote: [id: string]
  closeFootnote: [id: string]
}>()

const pageRef = useTemplateRef('page')

/* Some pages have a page number explicitly defined in the pageNumberString field,
while others only have it implicitly in the bodytext. We prefer the explicit one when available,
but fall back to the bodytext one if not. */
const explicitPageNumber = computed(() => {
  const pageNumber = props.page.pageNumberString?.trim()
  if (!pageNumber || pageNumber.toLowerCase() === 'null') {
    return undefined
  }

  return pageNumber
})

const bodyPageNumbers = computed(() => getBodyPageNumbers(props.page.bodytext))

const resolvedPageNumber = computed(() => {
  return explicitPageNumber.value ?? bodyPageNumbers.value[0]
})

watch(pageRef, (page) => {
  if (page && resolvedPageNumber.value) {
    emit('mounted', page, resolvedPageNumber.value)
  }
})

onBeforeUnmount(() => {
  if (resolvedPageNumber.value) {
    emit('beforeUnmount', pageRef.value, resolvedPageNumber.value)
  }
})

const { $parseStringToDocument } = useNuxtApp()

const htmlParserPlugins = computed<HtmlParserPlugins>(() => {
  const plugins: HtmlParserPlugins = ['process-links', 'strip-epub-resources', 'setup-footnote-containers']
  if (props.page.secondLevelNodeType === 'book_title') {
    plugins.push(createBookTitlePlugin(props.book))
  }

  return plugins
})

function renderBodyContent(html: string): string {
  if (!html) {
    return ''
  }

  const processedContent = $parseStringToDocument(html, htmlParserPlugins.value, { source: 'epub' })?.body.innerHTML ?? html

  const highlightedContent = props.searchResult?.id?.toLowerCase() === props.page.id.toLowerCase()
    ? handleHighlights(processedContent, props.searchResult.highlights, props.searchResult.id)
    : processedContent

  const withFootnotes = $parseStringToDocument(highlightedContent, ['insert-footnotes'], {
    content: Object.values(props.footnotes).flatMap(it => it.open && it.content ? [it.content] : []),
  })

  return withFootnotes.body.innerHTML || highlightedContent
}

const bodyText = computed(() => {
  return renderBodyContent(props.page.bodytext ?? '')
})

const hasMissingPageNumber = computed(() => !explicitPageNumber.value)

/* For pages without an explicit page number and multiple pages in the body text,
we try to split the body text into segments based on page numbers in the text.
This allows us to show the correct page number for each segment. */
const virtualPageSegments = computed(() => {
  if (!hasMissingPageNumber.value) {
    return []
  }

  return splitVirtualPageSegments(props.page.bodytext ?? '')
    .map(segment => ({
      pageNumber: segment.pageNumber,
      html: renderBodyContent(segment.html),
    }))
    .filter(segment => !!segment.html)
})

const showVirtualPageSegments = computed(() => virtualPageSegments.value.length > 1)

const showPageNumber = computed(() => {
  const page = props.page

  return (showVirtualPageSegments.value || resolvedPageNumber.value)
    && page.secondLevelNodeType
    && !hidePageNumberOnSecondLevelPageNodeType.includes(page.secondLevelNodeType)
})

function handleClick({ target }: Event) {
  let footnoteId: string | undefined

  if (target instanceof HTMLElement) {
    footnoteId = target.dataset.footnoteId
  }
  else if (target instanceof Element) {
    footnoteId = target.parentElement?.dataset.footnoteId
  }

  if (!footnoteId) {
    return
  }

  const state = props.footnotes[footnoteId]

  if (state?.open) {
    emit('closeFootnote', footnoteId)
  }
  else {
    emit('openFootnote', footnoteId)
  }
}
</script>

<template>
  <div
    ref="page"
    data-citation-source="literature"
    data-marking-content-type="book"
    :data-marking-work-id="book.id"
    :data-marking-document-id="book.id"
    :data-marking-unit-id="props.page.id"
    :data-marking-previous-edition-id="book.previousEdition"
    :data-citation-date-of-publication="book.dateOfPublication"
    :data-citation-title="book.title"
    :data-citation-authors="book.author?.join(',') || ''"
    :data-citation-publisher="book.publisher?.[0] || ''"
    :data-citation-year="book.year || ''"
    :data-citation-page="showVirtualPageSegments ? undefined : resolvedPageNumber"
    :data-citation-isbn="book.isbn?.join(',')"
    :data-citation-edition-number="book.editionNumber ?? undefined"
  >
    <template v-if="showVirtualPageSegments">
      <div
        v-for="segment in virtualPageSegments"
        :key="segment.pageNumber"
        class="flex"
        :data-citation-page="segment.pageNumber"
      >
        <div class="page-number xs:max-md:hidden flex text-caption mr-l mt-3xs shrink-0 w-12 select-none">
          <span v-if="showPageNumber">
            Side {{ segment.pageNumber }}
          </span>
        </div>
        <BodyText
          v-if="segment.html"
          data-body
          class="grow"
          source="epub"
          :html="segment.html"
          @click="handleClick"
        />
      </div>
    </template>
    <div v-else class="flex">
      <div class="page-number xs:max-md:hidden text-caption mr-l mt-3xs shrink-0 w-12">
        <span v-if="showPageNumber">
          Side {{ resolvedPageNumber }}
        </span>
      </div>
      <BodyText
        v-if="bodyText"
        data-body
        class="grow"
        source="epub"
        :html="bodyText"
        @click="handleClick"
      />
    </div>
  </div>
</template>

<style scoped lang="postcss">
:deep(.body-text) {
  h1 {
    @apply font-body text-2xl sm:text-3xl font-semibold mb-m;
  }
  h1 .subtitle {
    @apply font-body text-xl sm:text-lg font-bold mb-m block;
  }
  h1.chaptitle {
    @apply font-body text-2xl sm:text-3xl font-semibold mb-m;
  }
  h2 {
    @apply font-body text-xl sm:text-lg font-bold mb-m;
  }
  h6 {
    @apply italic font-normal text-sm sm:text-sm;
  }

  /* h3, h4 handled by BodyText */

  h1 span {
    @apply border-0 !important;
  }

  h1.chaptitle span.chap_no,
  h1.chaptitle span.chap_no1,
  h1.chaptitle span.chap_no2 {
    @apply block mb-2xs;
  }
  h1.chaptitle span.chap_no {
    @apply font-body text-2xl sm:text-3xl font-semibold;
  }
  h1.chaptitle span.chap_no small {
    @apply lowercase font-body text-2xl sm:text-3xl font-semibold;
  }
  h1.chaptitle span.chap_no1 {
    @apply font-body text-xl sm:text-2xl font-semibold;
  }
  h1.chaptitle span.chap_no2 {
    @apply font-body text-xl sm:text-lg font-normal italic;
  }

  /* p, table, td, img, figcaption, lists handled by BodyText */

  /* 44px touch target for superscript footnote markers, without changing their visual size */
  @media (pointer: coarse) {
    sup [data-footnote-id] {
      @apply relative;

      &::after {
        content: '';
        @apply absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 size-11;
      }
    }
  }
}
</style>
